import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { supabase } from '../../utils/supabase';

function MeetingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // State Management
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Load meetings from meetings table
  useEffect(() => {
    loadMeetings();
  }, [user?.id]);

  const loadMeetings = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase
        .from('meetings')
        .select('*')
        .eq('user_id', user.id)
        .order('sent_at', { ascending: false });

      if (fetchError) throw fetchError;

      setMeetings(data || []);
    } catch (err) {
      console.error('Error loading meetings:', err);
      setError('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  // Filter meetings
  const filteredMeetings = meetings.filter(meeting => {
    const matchesSearch = 
      meeting.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      meeting.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      meeting.profession?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || meeting.reply_status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredMeetings.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const paginatedMeetings = filteredMeetings.slice(startIdx, startIdx + itemsPerPage);

  // Get unique statuses for filter
  const statuses = [...new Set(meetings.map(m => m.reply_status).filter(Boolean))];

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'replied':
        return 'bg-green-500/10 text-green-700 border-green-500/30';
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-700 border-yellow-500/30';
      case 'no_reply':
        return 'bg-red-500/10 text-red-700 border-red-500/30';
      case 'scheduled':
        return 'bg-blue-500/10 text-blue-700 border-blue-500/30';
      default:
        return 'bg-gray-500/10 text-gray-700 border-gray-500/30';
    }
  };

  // Handle send reply
  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      setError('Reply message cannot be empty');
      return;
    }

    try {
      const { error: updateError } = await supabase
        .from('meetings')
        .update({
          reply_message: replyMessage,
          reply_status: 'replied',
          sent_at: new Date().toISOString()
        })
        .eq('id', selectedMeeting.id);

      if (updateError) throw updateError;

      // Reload meetings
      await loadMeetings();
      setShowReplyModal(false);
      setReplyMessage('');
      setError('');
    } catch (err) {
      console.error('Error sending reply:', err);
      setError('Failed to send reply');
    }
  };

  return (
    <DashboardLayout title="Meetings" currentPath="/meetings">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* Header with Search and Filters */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-2xl font-headline-bold text-foreground mb-4">Scheduled Meetings</h2>
          
          <div className="grid md:grid-cols-3 gap-4">
            <Input
              placeholder="Search by name, email, or profession..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              icon="Search"
            />
            
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Status</option>
              {statuses.map(status => (
                <option key={status} value={status}>
                  {status?.replace(/_/g, ' ').charAt(0).toUpperCase() + status?.slice(1)}
                </option>
              ))}
            </Select>

            <div className="flex items-end gap-2">
              <Button
                variant="secondary"
                icon={<Icon name="RefreshCw" />}
                onClick={loadMeetings}
                full
              >
                Refresh
              </Button>
            </div>
          </div>

          <div className="flex gap-4 mt-4">
            {['Total', 'Replied', 'Scheduled', 'Pending'].map((label, idx) => {
              let count = 0;
              if (label === 'Total') count = meetings.length;
              if (label === 'Replied') count = meetings.filter(m => m.reply_status === 'replied').length;
              if (label === 'Scheduled') count = meetings.filter(m => m.reply_status === 'scheduled').length;
              if (label === 'Pending') count = meetings.filter(m => m.reply_status === 'pending').length;

              return (
                <div key={idx} className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground font-medium">{label}</p>
                  <p className="text-2xl font-bold text-foreground">{count}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Error State */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/10 border border-red-500 rounded-lg p-4 text-red-600 flex items-center gap-2"
          >
            <Icon name="AlertCircle" size={20} />
            {error}
          </motion.div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading meetings...</p>
            </div>
          </div>
        ) : filteredMeetings.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-card border border-border rounded-lg p-12 text-center"
          >
            <Icon name="Calendar" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-headline-semibold text-foreground mb-2">No Meetings Found</h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your filters' 
                : 'No scheduled meetings yet'}
            </p>
          </motion.div>
        ) : (
          <>
            {/* Meetings Table */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-card border border-border rounded-lg overflow-hidden"
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">Contact</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">Email</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">Profession</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">Status</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">Meeting Link</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">Sent</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-foreground">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence>
                      {paginatedMeetings.map((meeting, index) => (
                        <motion.tr
                          key={meeting.id}
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ delay: index * 0.05 }}
                          className="border-b border-border hover:bg-muted/50 transition-colors"
                        >
                          <td className="px-6 py-4">
                            <p className="font-medium text-foreground">{meeting.name || 'N/A'}</p>
                          </td>
                          <td className="px-6 py-4">
                            <a
                              href={`mailto:${meeting.email}`}
                              className="text-primary hover:underline text-sm"
                            >
                              {meeting.email || 'N/A'}
                            </a>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-muted-foreground">
                              {meeting.profession || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(meeting.reply_status)}`}>
                              {meeting.reply_status?.replace(/_/g, ' ').charAt(0).toUpperCase() + meeting.reply_status?.slice(1) || 'Pending'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {meeting.meeting_link ? (
                              <a
                                href={meeting.meeting_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline text-sm"
                              >
                                <Icon name="Link" size={16} className="inline mr-1" />
                                Join
                              </a>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-muted-foreground">
                              {formatDate(meeting.sent_at)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setSelectedMeeting(meeting);
                                setShowDetailModal(true);
                              }}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 hover:bg-primary/20 text-primary rounded text-sm font-medium transition-colors"
                            >
                              <Icon name="Eye" size={14} />
                              View
                            </motion.button>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  icon={<Icon name="ChevronLeft" size={16} />}
                >
                  Previous
                </Button>

                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted hover:bg-muted/80 text-foreground'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  icon={<Icon name="ChevronRight" size={16} />}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}

        {/* Meeting Detail Modal */}
        <AnimatePresence>
          {showDetailModal && selectedMeeting && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowDetailModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-96 overflow-y-auto"
              >
                {/* Modal Header */}
                <div className="bg-muted/50 border-b border-border p-6 flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-headline-bold text-foreground">
                      {selectedMeeting.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">{selectedMeeting.profession}</p>
                  </div>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name="X" size={24} />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="p-6 space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase">Email</label>
                    <a href={`mailto:${selectedMeeting.email}`} className="text-primary hover:underline mt-1">
                      {selectedMeeting.email}
                    </a>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase">Status</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(selectedMeeting.reply_status)}`}>
                        {selectedMeeting.reply_status?.replace(/_/g, ' ').charAt(0).toUpperCase() + selectedMeeting.reply_status?.slice(1) || 'Pending'}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase">Message</label>
                    <p className="text-foreground mt-1 bg-muted/50 p-3 rounded">
                      {selectedMeeting.message || 'No message'}
                    </p>
                  </div>

                  {selectedMeeting.reply_message && (
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Reply</label>
                      <p className="text-foreground mt-1 bg-primary/10 p-3 rounded">
                        {selectedMeeting.reply_message}
                      </p>
                    </div>
                  )}

                  {selectedMeeting.meeting_link && (
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground uppercase">Meeting Link</label>
                      <a
                        href={selectedMeeting.meeting_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline mt-1"
                      >
                        Join Meeting
                      </a>
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-semibold text-muted-foreground uppercase">Sent At</label>
                    <p className="text-foreground mt-1">{formatDate(selectedMeeting.sent_at)}</p>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="border-t border-border p-6 flex justify-end gap-3">
                  {selectedMeeting.reply_status !== 'replied' && (
                    <Button
                      variant="primary"
                      onClick={() => {
                        setShowDetailModal(false);
                        setShowReplyModal(true);
                      }}
                      icon={<Icon name="Send" size={16} />}
                    >
                      Send Reply
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => setShowDetailModal(false)}
                  >
                    Close
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Reply Modal */}
        <AnimatePresence>
          {showReplyModal && selectedMeeting && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowReplyModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-card border border-border rounded-lg max-w-xl w-full"
              >
                {/* Header */}
                <div className="bg-muted/50 border-b border-border p-6 flex justify-between items-center">
                  <h3 className="text-lg font-headline-bold text-foreground">
                    Reply to {selectedMeeting.name}
                  </h3>
                  <button
                    onClick={() => setShowReplyModal(false)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name="X" size={24} />
                  </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-foreground block mb-2">Your Reply</label>
                    <textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your reply message here..."
                      className="w-full h-32 px-4 py-3 bg-muted border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {replyMessage.length} characters
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="border-t border-border p-6 flex justify-end gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => setShowReplyModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSendReply}
                    disabled={!replyMessage.trim()}
                    icon={<Icon name="Send" size={16} />}
                  >
                    Send Reply
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </DashboardLayout>
  );
}

export default MeetingsPage;
